import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Stack,
  Fade,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLocale } from '../../hooks/useLocale';
import { LocaleKey } from '../../common/localization';

interface TranslationProgressModalProps {
  open: boolean;
  progress: number;
  /** Optional display name for target language; falls back to current UI language name */
  targetLanguage?: string;
}

const BRAND_GRADIENT = 'linear-gradient(135deg, #2979FF 0%, #00B0FF 100%)';
const GLOW_GRADIENT = 'linear-gradient(135deg, rgba(41,121,255,0.45) 0%, rgba(0,176,255,0.25) 50%, rgba(124,77,255,0.35) 100%)';

const FLAVOR_KEYS: LocaleKey[] = [
  'translate_progress_flavor_1',
  'translate_progress_flavor_2',
  'translate_progress_flavor_3',
  'translate_progress_flavor_4',
  'translate_progress_flavor_5',
];
const FLAVOR_ROTATE_MS = 3500;
const FLAVOR_FADE_MS = 260;

const TranslationProgressModal: React.FC<TranslationProgressModalProps> = ({
  open,
  progress,
  targetLanguage,
}) => {
  const theme = useTheme();
  const { getLanguageName, language } = useLanguage();
  const { t } = useLocale();
  const isDark = theme.palette.mode === 'dark';

  const surface = isDark ? '#1A1D2E' : '#FFFFFF';
  const textPrimary = isDark ? '#F5F6FA' : '#13151F';
  const textSecondary = isDark ? '#A4AAC6' : '#5A6178';
  const borderColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(19,21,31,0.08)';
  const trackBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(19,21,31,0.06)';

  const resolvedTarget = targetLanguage ?? getLanguageName(language);

  const stepLabel = progress >= 99.5
    ? t('translate_progress_step_done')
    : progress < 25
      ? t('translate_progress_step_analyze')
      : progress < 70
        ? t('translate_progress_step_generate')
        : t('translate_progress_step_pack');

  // Rotating flavor text — picks a new random entry every FLAVOR_ROTATE_MS while progress is active.
  const [flavorIndex, setFlavorIndex] = useState(0);
  const [flavorVisible, setFlavorVisible] = useState(true);

  useEffect(() => {
    if (!open) return;
    // Reset to first flavor each time the modal opens
    setFlavorIndex(0);
    setFlavorVisible(true);
  }, [open]);

  useEffect(() => {
    if (!open || progress >= 100) return;
    const rotate = setInterval(() => {
      // Fade out, swap, fade in
      setFlavorVisible(false);
      const swap = setTimeout(() => {
        setFlavorIndex((i) => (i + 1) % FLAVOR_KEYS.length);
        setFlavorVisible(true);
      }, FLAVOR_FADE_MS);
      // Guard against teardown races
      return () => clearTimeout(swap);
    }, FLAVOR_ROTATE_MS);
    return () => clearInterval(rotate);
  }, [open, progress]);

  const flavorText = useMemo(
    () => t(FLAVOR_KEYS[flavorIndex]),
    [t, flavorIndex],
  );
  const showFlavor = progress < 99.5;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: '16px', sm: '32px' },
          width: { xs: 'calc(100% - 32px)', sm: 420 },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 420 },
          borderRadius: '24px',
          background: surface,
          color: textPrimary,
          border: `1px solid ${borderColor}`,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(41,121,255,0.12)'
            : '0 24px 60px rgba(19,21,31,0.18), 0 0 0 1px rgba(41,121,255,0.08)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(5,6,15,0.72)' : 'rgba(19,21,31,0.55)',
          backdropFilter: 'blur(8px)',
        },
      }}
    >
      {/* Top brand stripe */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 3,
          background: BRAND_GRADIENT,
        }}
      />
      {/* Ambient glow */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -100,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 380,
          height: 220,
          background: GLOW_GRADIENT,
          filter: 'blur(60px)',
          opacity: isDark ? 0.55 : 0.35,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{
        position: 'relative',
        px: { xs: 3, sm: 4 },
        py: { xs: 4, sm: 5 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 2.5, sm: 3 },
      }}>
        {/* Circular progress with inline percentage */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={96}
            thickness={3}
            sx={{
              color: trackBg,
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          />
          <CircularProgress
            variant="determinate"
            value={progress}
            size={96}
            thickness={3}
            sx={{
              color: '#00B0FF',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"SF Pro Display", "Inter", sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
                color: textPrimary,
                lineHeight: 1,
              }}
            >
              {Math.round(progress)}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: textSecondary,
              }}
            >
              %
            </Typography>
          </Box>
        </Box>

        {/* Title + eyebrow */}
        <Stack spacing={0.75} alignItems="center">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <AutoAwesomeRoundedIcon sx={{ fontSize: 14, color: '#00B0FF' }} />
            <Typography sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#00B0FF',
            }}>
              AI · {resolvedTarget}
            </Typography>
          </Stack>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"SF Pro Display", "Inter", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.375rem' },
              textAlign: 'center',
              color: textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            {t('translate_progress_title')}
          </Typography>
          <Typography sx={{
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            color: textSecondary,
            textAlign: 'center',
            lineHeight: 1.55,
          }}>
            {t('translate_progress_subtitle', { targetLang: resolvedTarget })}
          </Typography>
        </Stack>

        {/* Linear progress */}
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 999,
              backgroundColor: trackBg,
              '& .MuiLinearProgress-bar': {
                background: BRAND_GRADIENT,
                borderRadius: 999,
              },
            }}
          />
          <Box sx={{ mt: 1.25, minHeight: 40, textAlign: 'center' }}>
            <Typography sx={{
              fontSize: '0.8125rem',
              color: textPrimary,
              fontWeight: 600,
              letterSpacing: '0.005em',
            }}>
              {stepLabel}
            </Typography>
            {showFlavor && (
              <Fade in={flavorVisible} timeout={FLAVOR_FADE_MS}>
                <Typography sx={{
                  mt: 0.25,
                  fontSize: '0.75rem',
                  color: textSecondary,
                  fontStyle: 'italic',
                }}>
                  {flavorText}
                </Typography>
              </Fade>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TranslationProgressModal;
